/**
 * P-05/F (D-43, D-52) — the pure model of "הוספת אבני דרך ומשימות": the tree
 * being edited, its validation (the same rules as the server's), and the
 * request body — with the map back from a server error path
 * ("milestones.0.subs.1.tasks.2") to the row it names. Pinned by
 * `src/utils/__tests__/bulkAddModel.test.ts`.
 */
import type { BulkAddBody, BulkAddTaskBody } from '../../services/api';

export interface BulkTask { key: string; title: string; assigneeIds: string[]; hours: string; due: string }
export interface BulkSub { key: string; existingId: string; name: string; due: string; tasks: BulkTask[] }
export interface BulkGroup {
  key: string;
  /** new milestone · existing milestone · tasks without a milestone */
  kind: 'new' | 'existing' | 'none';
  existingId: string;
  name: string;
  due: string;
  tasks: BulkTask[];
  subs: BulkSub[];
}

let seq = 0;
export const newKey = (prefix: string): string => `${prefix}-${++seq}`;
export const emptyTask = (): BulkTask => ({ key: newKey('t'), title: '', assigneeIds: [], hours: '', due: '' });
export const emptySub = (existingId = ''): BulkSub => ({ key: newKey('s'), existingId, name: '', due: '', tasks: [emptyTask()] });
export const emptyGroup = (kind: BulkGroup['kind'], existingId = ''): BulkGroup =>
  ({ key: newKey('g'), kind, existingId, name: '', due: '', tasks: [emptyTask()], subs: [] });

/** Field errors by `${rowKey}:${field}` (field = title / hours / assignees / due / name / existing). */
export type BulkErrors = Record<string, string>;

const blank = (s: string) => s.trim() === '';

/** The same rules as the server (bulkAdd.service): name 3–500, estimate * (a number ≥ 0); milestone name 2–255. */
export function validateBulk(groups: BulkGroup[], msg: (k: string) => string): BulkErrors {
  const e: BulkErrors = {};
  const task = (t: BulkTask) => {
    const title = t.title.trim();
    if (title.length < 3 || title.length > 500) e[`${t.key}:title`] = msg('bulkAdd.errors.title');
    const n = Number(t.hours.replace(',', '.'));
    if (!blank(t.hours) && (!Number.isFinite(n) || n <= 0)) e[`${t.key}:hours`] = msg('bulkAdd.errors.hours');
  };
  const named = (key: string, name: string) => {
    const n = name.trim();
    if (n.length < 2 || n.length > 255) e[`${key}:name`] = msg('bulkAdd.errors.milestoneName');
  };
  for (const g of groups) {
    if (g.kind === 'new') named(g.key, g.name);
    if (g.kind === 'existing' && !g.existingId) e[`${g.key}:existing`] = msg('bulkAdd.errors.pickMilestone');
    g.tasks.forEach(task);
    for (const s of g.subs) {
      if (!s.existingId) named(s.key, s.name);
      s.tasks.forEach(task);
    }
  }
  return e;
}

/** D-75 — how many different workers were assigned to the new tasks (the one "לשייך גם?" question). */
export const countBulkAssignees = (groups: BulkGroup[]): number => {
  const ids = new Set<string>();
  for (const g of groups) {
    for (const t of g.tasks) t.assigneeIds.forEach((id) => ids.add(id));
    for (const s of g.subs) for (const t of s.tasks) t.assigneeIds.forEach((id) => ids.add(id));
  }
  return ids.size;
};

export const countBulkTasks = (groups: BulkGroup[]): number =>
  groups.reduce((n, g) => n + g.tasks.length + g.subs.reduce((x, s) => x + s.tasks.length, 0), 0);

export const countBulkMilestones = (groups: BulkGroup[]): number =>
  groups.reduce((n, g) => n + (g.kind === 'new' ? 1 : 0) + g.subs.filter((s) => !s.existingId).length, 0);

const taskBody = (t: BulkTask): BulkAddTaskBody => ({
  title: t.title.trim(),
  ...(t.assigneeIds.length > 0 ? { assignee_ids: [...t.assigneeIds] } : {}),
  ...(blank(t.hours) ? {} : { estimated_hours: Number(t.hours.replace(',', '.')) }),
  ...(t.due ? { due_date: t.due } : {}),
});

/**
 * The request body, and the map from each server error path ("milestones.0",
 * "milestones.0.tasks.1", "milestones.0.subs.0", "tasks.2") to the row key.
 */
export function buildBulkBody(groups: BulkGroup[]): { body: BulkAddBody; pathToKey: Record<string, string> } {
  const body: BulkAddBody = { milestones: [], tasks: [] };
  const pathToKey: Record<string, string> = {};
  for (const g of groups) {
    if (g.kind === 'none') {
      for (const t of g.tasks) {
        pathToKey[`tasks.${body.tasks!.length}`] = t.key;
        body.tasks!.push(taskBody(t));
      }
      continue;
    }
    const i = body.milestones!.length;
    pathToKey[`milestones.${i}`] = g.key;
    g.tasks.forEach((t, k) => { pathToKey[`milestones.${i}.tasks.${k}`] = t.key; });
    g.subs.forEach((s, j) => {
      pathToKey[`milestones.${i}.subs.${j}`] = s.key;
      s.tasks.forEach((t, k) => { pathToKey[`milestones.${i}.subs.${j}.tasks.${k}`] = t.key; });
    });
    body.milestones!.push({
      ...(g.kind === 'existing' ? { existing_id: g.existingId } : { name: g.name.trim(), ...(g.due ? { due_date: g.due } : {}) }),
      tasks: g.tasks.map(taskBody),
      subs: g.subs.map((s) => ({
        ...(s.existingId ? { existing_id: s.existingId } : { name: s.name.trim(), ...(s.due ? { due_date: s.due } : {}) }),
        tasks: s.tasks.map(taskBody),
      })),
    });
  }
  return { body, pathToKey };
}

const FIELD_OF: Record<string, string> = { title: 'title', estimated_hours: 'hours', assignee_ids: 'assignees', due_date: 'due', name: 'name', existing_id: 'existing' };

/** The server's `details.errors` as this form's errors. */
export function serverBulkErrors(errors: { path: string; field: string; message: string }[], pathToKey: Record<string, string>): BulkErrors {
  const out: BulkErrors = {};
  for (const err of errors) {
    const key = pathToKey[err.path];
    if (key) out[`${key}:${FIELD_OF[err.field] ?? err.field}`] = err.message;
  }
  return out;
}
